import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { getDashboardStats, getEmployees, getLeaves, getAttendance, getBgvDashboardStats } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Building2, FileText, UserCheck, ShieldCheck, ArrowRight, Clock, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const AdminDashboard = ({ user, setUser }) => {
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [bgvStats, setBgvStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const results = await Promise.allSettled([
        getDashboardStats(),
        getEmployees(),
        getLeaves(),
        getAttendance(),
        getBgvDashboardStats(7),
      ]);
      if (results[0].status === 'fulfilled') setStats(results[0].value.data);
      if (results[1].status === 'fulfilled') setEmployees(results[1].value.data || []);
      if (results[2].status === 'fulfilled') setLeaves(results[2].value.data || []);
      if (results[3].status === 'fulfilled') setAttendance(results[3].value.data || []);
      if (results[4].status === 'fulfilled') setBgvStats(results[4].value.data);
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length === results.length) {
        toast.error('Failed to fetch dashboard data');
      } else if (failed.length > 0) {
        console.warn('Some dashboard calls failed:', failed.map(f => f.reason?.message));
      }
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Employees', value: stats?.total_employees || 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { title: 'Departments', value: stats?.total_departments || 0, icon: Building2, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { title: 'Pending Leaves', value: stats?.pending_leaves || 0, icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50' },
    { title: 'Present Today', value: stats?.present_today || 0, icon: UserCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  ];

  const leaveStatusData = [
    { name: 'Pending', value: leaves.filter(l => l.status === 'pending').length, color: '#f59e0b' },
    { name: 'Approved', value: leaves.filter(l => l.status === 'approved').length, color: '#10b981' },
    { name: 'Rejected', value: leaves.filter(l => l.status === 'rejected').length, color: '#ef4444' },
  ];

  // Calculate actual attendance data by month
  const getAttendanceByMonth = () => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();
    const last6Months = [];
    
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      const monthName = monthNames[monthIndex];
      
      // Filter attendance for this month
      const monthAttendance = attendance.filter(a => {
        const attDate = new Date(a.date);
        return attDate.getMonth() === monthIndex;
      });
      
      const presentCount = monthAttendance.filter(a => a.status === 'present' || a.status === 'half_day').length;
      const absentCount = monthAttendance.filter(a => a.status === 'absent').length;
      
      last6Months.push({
        month: monthName,
        present: presentCount,
        absent: absentCount,
      });
    }
    
    return last6Months;
  };

  const attendanceByMonth = getAttendanceByMonth();

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-8" data-testid="admin-dashboard">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="dashboard-title">Dashboard Overview</h1>
          <p className="text-slate-600 mt-1">Your workforce analytics at a glance</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border border-slate-100 shadow-sm hover:border-indigo-100 transition-colors card-hover" data-testid={`stat-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-900 mt-2 mono" data-testid={`stat-value-${index}`}>{stat.value}</p>
                    </div>
                    <div className={`${stat.bg} ${stat.color} p-4 rounded-xl`}>
                      <Icon size={28} strokeWidth={1.5} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* BGV Quick Access */}
        <Link to="/bgv" className="block group" data-testid="dashboard-bgv-tile">
          <Card className="border border-slate-100 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <ShieldCheck size={32} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Background Verification</h3>
                    <p className="text-white/80 text-sm mt-1">
                      Standard 5-check package · Identity · Address · Employment · Education · Criminal
                    </p>
                  </div>
                </div>
                {bgvStats && (
                  <div className="flex gap-6 items-center">
                    <div className="text-center">
                      <p className="text-2xl font-bold" data-testid="bgv-tile-total">{bgvStats.total_cases}</p>
                      <p className="text-[10px] uppercase tracking-wide text-white/70">Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-200">
                        {bgvStats.by_final_result.clear}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-white/70">Clear</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-200 flex items-center gap-1">
                        {(bgvStats.stuck_cases || []).length > 0 && (
                          <AlertTriangle size={14} className="animate-pulse" />
                        )}
                        {(bgvStats.stuck_cases || []).length}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-white/70">Stuck &gt; 7d</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold flex items-center gap-1">
                        <Clock size={14} />
                        {bgvStats.tat_hours?.avg != null ? `${bgvStats.tat_hours.avg}h` : '—'}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-white/70">Avg TAT</p>
                    </div>
                    <ArrowRight
                      size={20}
                      className="transform group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                )}
                {!bgvStats && <ArrowRight size={20} />}
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Attendance Chart */}
          <Card className="lg:col-span-8 border border-slate-100 shadow-sm" data-testid="attendance-chart-card">
            <CardHeader>
              <CardTitle>Attendance Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={attendanceByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <Bar dataKey="present" fill="#4f46e5" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="absent" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Leave Status Pie */}
          <Card className="lg:col-span-4 border border-slate-100 shadow-sm" data-testid="leave-status-card">
            <CardHeader>
              <CardTitle>Leave Status</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={leaveStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {leaveStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {leaveStatusData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-slate-600">{item.name}</span>
                    </div>
                    <span className="font-semibold text-slate-900">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="border border-slate-100 shadow-sm" data-testid="recent-activity-card">
          <CardHeader>
            <CardTitle>Recent Leave Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {leaves.slice(0, 5).length === 0 ? (
              <p className="text-slate-500 text-center py-8">No leave requests yet</p>
            ) : (
              <div className="space-y-3">
                {leaves.slice(0, 5).map((leave, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <div>
                      <p className="font-medium text-slate-900">Leave Request</p>
                      <p className="text-sm text-slate-600">{leave.start_date} - {leave.end_date}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      leave.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                      leave.status === 'approved' ? 'bg-emerald-50 text-emerald-600' :
                      'bg-rose-50 text-rose-600'
                    }`}>
                      {leave.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminDashboard;